import * as React from "react";
import { useEffect, useState } from "react";
import { getDataTestId } from "../../../../utils/getDataTestId";
import {
  WSElement,
  WSElementProps,
  detachLayoutProps
} from "../../WSElement/WSElement.component";
import { WSPanel } from "../../core/WSPanel/WSPanel.component";
import { WSText } from "../../core/WSText/WSText.component";
import styles from "./WSHorizontalScrollCards.module.scss";

export type WSHorizontalScrollCardsEndCardConfig = {
  onClick: () => void;
  label: string;
};

export interface WSHorizontalScrollCardsProps extends WSElementProps {
  cardWidth?: number;
  endCard?: WSHorizontalScrollCardsEndCardConfig;
  name?: string;
}

export const WSHorizontalScrollCards: React.FC<WSHorizontalScrollCardsProps> = ({
  children,
  cardWidth = 200,
  endCard,
  name,
  ...otherProps
}) => {
  const { layoutProps } = detachLayoutProps(otherProps);

  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const gapWidth = 24;
    let itemsCount = React.Children.count(children);
    if (endCard) {
      itemsCount += 1;
    }

    setContainerWidth(cardWidth * itemsCount + gapWidth * (itemsCount - 1));
  }, [endCard, children, cardWidth]);

  const itemStyles: React.CSSProperties = {
    width: cardWidth
  };

  const testIdBase = getDataTestId("horizontalScrollCards", name);

  return (
    <WSElement
      className={styles.cardsContainer}
      data-testid={testIdBase}
      role="region"
      aria-label="Scrollable cards"
      {...layoutProps}
    >
      <div style={{ width: containerWidth }} className={styles.gridContainer}>
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return (
              <div
                className={styles.item}
                style={itemStyles}
                data-testid={`${testIdBase}Item-${index}`}
              >
                {child}
              </div>
            );
          }
          return null;
        })}
        {endCard && (
          <div className={styles.item} style={itemStyles}>
            <WSPanel
              className={styles.endCard}
              onClick={endCard.onClick}
              data-testid={`${testIdBase}EndCard`}
            >
              <WSText weight="medium" color="blue400">
                {endCard.label}
              </WSText>
            </WSPanel>
          </div>
        )}
      </div>
    </WSElement>
  );
};
